import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import moodTrackerService from '../../services/moodTrackerService';
import analyticsService from '../../services/analyticsService';
import { MOOD_OPTIONS, MOOD_TRACKER_MESSAGES } from '../../utils/moodTrackerUtils';
import { Button, Card, Input, Spinner } from '../design-system';
import { toast } from 'react-hot-toast';

const MoodTracker = ({ hideTitle = false, customTitle, customSubtext, onMoodSubmitted }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState('mood-selection'); // 'mood-selection', 'comment', 'completed'
  const [selectedMood, setSelectedMood] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check if mood has been submitted today on component mount
  useEffect(() => {
    const checkTodaysMood = async () => {
      try {
        const hasSubmitted = await moodTrackerService.isMoodSubmittedToday(user);
        if (hasSubmitted) {
          // If already submitted today, show completed state
          setCurrentStep('completed');
          // Notify parent that mood was already submitted (for transition)
          if (onMoodSubmitted) {
            // Small delay to ensure UI is ready
            setTimeout(() => {
              onMoodSubmitted();
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error checking today\'s mood:', error);
      }
    };

    checkTodaysMood();
  }, [user, onMoodSubmitted]);

  const handleMoodSelection = (mood) => {
    setSelectedMood(mood);
    setCurrentStep('comment');
  };

  const handleSkip = async () => {
    await submitMood('');
  };

  const handleComplete = async () => {
    await submitMood(comment);
  };

  const submitMood = async (moodComment) => {
    if (!selectedMood) return;

    setSubmitting(true);
    try {
      await moodTrackerService.submitMood({
        mood: selectedMood.id,
        comment: moodComment
      }, user);

      setCurrentStep('completed');
      toast.success('Mood recorded successfully!');
      
      // Track mood submission event
      try {
        analyticsService.track('mood_submitted', {
          mood: selectedMood.id,
          moodLabel: selectedMood.label,
          hasComment: !!moodComment,
          commentLength: moodComment?.length || 0
        });
      } catch (analyticsError) {
        // Don't break mood submission if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
      
      // Notify parent component that mood was submitted
      if (onMoodSubmitted) {
        onMoodSubmitted();
      }
    } catch (error) {
      console.error('Error submitting mood:', error);
      toast.error('Failed to record mood. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetMoodTracker = () => {
    setCurrentStep('mood-selection');
    setSelectedMood(null);
    setComment('');
  };

  // Render mood selection step
  const renderMoodSelection = () => (
    <div className="text-center">
      {!hideTitle && (
        <div className="mood-survey-title-container">
          <h2 className="mood-survey-title">
            {customTitle || "Share how you're feeling"}
          </h2>
          <p className="mood-survey-subtitle-text">
            {customSubtext || "Your therapist uses this to understand your week better."}
          </p>
        </div>
      )}
      
      <div className="mood-options-container">
        {MOOD_OPTIONS.map((mood) => (
          <button
            key={mood.id}
            onClick={() => handleMoodSelection(mood)}
            className="mood-option-button"
            aria-label={`Select ${mood.label} mood`}
          >
            <div 
              className="mood-option-emoji"
              style={{ backgroundColor: `${mood.color}33` }}
            >
              {mood.emoji}
            </div>
            <span className="mood-option-label">
              {mood.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  // Render comment step
  const renderCommentStep = () => (
    <div className="text-center">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {selectedMood.followUpQuestion}
      </h3>
      
      <div className="mb-6">
        <Input
          type="textarea"
          placeholder="Share what's on your mind (optional)..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="w-full min-h-[80px] resize-none"
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-1 text-right">
          {comment.length}/500 characters
        </p>
      </div>
      
      <div className="flex gap-3 justify-center">
        <Button
          variant="outline"
          onClick={handleSkip}
          disabled={submitting}
          className="px-6"
        >
          {MOOD_TRACKER_MESSAGES.skipText}
        </Button>
        <Button
          onClick={handleComplete}
          disabled={submitting}
          className="px-6"
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Submitting...
            </>
          ) : (
            MOOD_TRACKER_MESSAGES.completeText
          )}
        </Button>
      </div>
    </div>
  );

  // Render completed step
  const renderCompletedStep = () => (
    <div className="text-center">
      <div className="mb-4">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-lg">
          <span className="text-2xl">✅</span>
          <span className="text-sm font-medium text-green-800">
            {selectedMood ? selectedMood.label : 'Mood recorded'}
          </span>
        </div>
      </div>
      
      <h3 className="text-lg font-semibold text-gray-800 mb-2">
        {MOOD_TRACKER_MESSAGES.completionMessage}
      </h3>
      
      <Button
        variant="outline"
        onClick={resetMoodTracker}
        className="mt-4 px-6"
      >
        Record Another Mood
      </Button>
    </div>
  );

  return (
    <Card className={hideTitle ? "p-4 mb-0" : "p-6 mb-6"}>
      <div className="mood-tracker-container">
        {currentStep === 'mood-selection' && renderMoodSelection()}
        {currentStep === 'comment' && renderCommentStep()}
        {currentStep === 'completed' && renderCompletedStep()}
      </div>
    </Card>
  );
};

export default MoodTracker;
